import { Injectable } from '@angular/core';
import type { SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { isCapacitorNative } from '../platform/capacitor-platform';
import { LoggerService } from '../utils/logger.service';
import type {
  PendingEventRow,
  PendingEventStatus,
  SyncEventType,
  SyncPullResponse,
  SyncStateValue,
} from '../sync/sync-event.types';
import type { ContentPackManifest, ContentPackQuestion } from '../content-pack/content-pack.types';

const DB_NAME = 'atplane_offline';

/**
 * On-device SQLite: sync outbox (3b) and content-pack metadata (3c+).
 */
@Injectable({ providedIn: 'root' })
export class LocalDatabaseService {
  private connection: SQLiteConnection | null = null;
  private db: SQLiteDBConnection | null = null;
  private initPromise: Promise<void> | null = null;

  get isNative(): boolean {
    return isCapacitorNative();
  }

  async isReady(): Promise<boolean> {
    if (!this.isNative) return false;
    await this.init();
    return this.db != null;
  }

  async init(): Promise<void> {
    if (!this.isNative) return;
    if (!this.initPromise) {
      this.initPromise = this.openNative();
    }
    return this.initPromise;
  }

  async saveOfflineEntitlement(userId: number, token: string, expiresAt: string): Promise<void> {
    const db = await this.requireDb();
    await db.run(
      `INSERT INTO local_offline_entitlement (user_id, token, expires_at, updated_at)
       VALUES (?, ?, ?, ?)
       ON CONFLICT(user_id) DO UPDATE SET
         token = excluded.token,
         expires_at = excluded.expires_at,
         updated_at = excluded.updated_at`,
      [userId, token, expiresAt, new Date().toISOString()]
    );
  }

  async loadOfflineEntitlement(userId: number): Promise<{ token: string; expiresAt: string } | null> {
    const db = await this.requireDb();
    const result = await db.query(
      `SELECT token, expires_at FROM local_offline_entitlement WHERE user_id = ? LIMIT 1`,
      [userId]
    );
    const row = result.values?.[0];
    return row
      ? { token: String(row['token']), expiresAt: String(row['expires_at']) }
      : null;
  }

  async clearOfflineEntitlements(userId?: number): Promise<void> {
    const db = await this.requireDb();
    if (userId == null) {
      await db.run(`DELETE FROM local_offline_entitlement`);
      return;
    }
    await db.run(`DELETE FROM local_offline_entitlement WHERE user_id = ?`, [userId]);
  }

  async insertPendingEvent(
    eventId: string,
    eventType: SyncEventType,
    payload: string
  ): Promise<void> {
    const db = await this.requireDb();
    const createdAt = new Date().toISOString();
    await db.run(
      `INSERT OR REPLACE INTO pending_events
        (event_id, event_type, payload, created_at, attempt_count, last_error, status)
       VALUES (?, ?, ?, ?, 0, NULL, 'pending')`,
      [eventId, eventType, payload, createdAt]
    );
  }

  async listPendingEvents(limit = 50): Promise<PendingEventRow[]> {
    const db = await this.requireDb();
    const res = await db.query(
      `SELECT event_id, event_type, payload, created_at, attempt_count, last_error, status
       FROM pending_events
       WHERE status IN ('pending', 'failed')
       ORDER BY created_at ASC, rowid ASC
       LIMIT ?`,
      [limit]
    );
    const rows = res.values ?? [];
    return rows.map((r) => ({
      eventId: String(r['event_id']),
      eventType: String(r['event_type']) as SyncEventType,
      payload: String(r['payload']),
      createdAt: String(r['created_at']),
      attemptCount: Number(r['attempt_count'] ?? 0),
      lastError: r['last_error'] != null ? String(r['last_error']) : null,
      status: String(r['status']) as PendingEventStatus,
    }));
  }

  async countPendingEvents(): Promise<number> {
    const db = await this.requireDb();
    const res = await db.query(
      `SELECT COUNT(*) AS c FROM pending_events WHERE status IN ('pending', 'failed')`
    );
    const row = res.values?.[0];
    return row ? Number(row['c'] ?? 0) : 0;
  }

  async markEventSyncing(eventId: string): Promise<void> {
    const db = await this.requireDb();
    await db.run(`UPDATE pending_events SET status = 'syncing' WHERE event_id = ?`, [eventId]);
  }

  async resetEventToPending(eventId: string): Promise<void> {
    const db = await this.requireDb();
    await db.run(`UPDATE pending_events SET status = 'pending' WHERE event_id = ?`, [eventId]);
  }

  async markEventDone(eventId: string): Promise<void> {
    const db = await this.requireDb();
    await db.run(`DELETE FROM pending_events WHERE event_id = ?`, [eventId]);
  }

  async markEventFailed(eventId: string, error: string, attemptCount: number): Promise<void> {
    const db = await this.requireDb();
    const status = attemptCount >= 8 ? 'failed' : 'pending';
    await db.run(
      `UPDATE pending_events SET status = ?, attempt_count = ?, last_error = ? WHERE event_id = ?`,
      [status, attemptCount, error, eventId]
    );
  }

  async setSyncState(
    state: SyncStateValue,
    opts?: { lastError?: string; clearError?: boolean }
  ): Promise<void> {
    const db = await this.requireDb();
    const lastSuccess = state === 'idle' ? new Date().toISOString() : null;
    const lastError = opts?.clearError ? null : (opts?.lastError ?? null);
    await db.run(
      `INSERT INTO sync_state (id, state, last_success_at, last_error)
       VALUES (1, ?, ?, ?)
       ON CONFLICT(id) DO UPDATE SET
         state = excluded.state,
         last_success_at = COALESCE(excluded.last_success_at, sync_state.last_success_at),
         last_error = excluded.last_error`,
      [state, lastSuccess, lastError]
    );
  }

  async getSyncMeta(key: string): Promise<string | null> {
    const db = await this.requireDb();
    const res = await db.query(`SELECT value FROM sync_meta WHERE key = ?`, [key]);
    const v = res.values?.[0]?.['value'];
    return v != null ? String(v) : null;
  }

  async setSyncMeta(key: string, value: string): Promise<void> {
    const db = await this.requireDb();
    await db.run(
      `INSERT INTO sync_meta (key, value) VALUES (?, ?)
       ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
      [key, value]
    );
  }

  /** Apply server pull deltas into local overlay tables (3b merge). */
  async mergePullResponse(pull: SyncPullResponse): Promise<void> {
    const db = await this.requireDb();
    const now = new Date().toISOString();
    for (const n of pull.notes ?? []) {
      await db.run(
        `INSERT INTO sync_pull_notes (question_id, notes, updated_at)
         VALUES (?, ?, ?)
         ON CONFLICT(question_id) DO UPDATE SET
           notes = excluded.notes,
           updated_at = excluded.updated_at`,
        [n.questionId, n.notes ?? '', n.notesChangedAt ?? now]
      );
    }
    for (const f of pull.flags ?? []) {
      await db.run(
        `INSERT INTO sync_pull_flags (question_id, flag_id, hide, updated_at)
         VALUES (?, ?, ?, ?)
         ON CONFLICT(question_id) DO UPDATE SET
           flag_id = excluded.flag_id,
           hide = excluded.hide,
           updated_at = excluded.updated_at`,
        [f.questionId, f.flagId ?? null, f.hide ?? 0, now]
      );
    }
    for (const a of pull.testAnswers ?? []) {
      await db.run(
        `INSERT INTO sync_pull_test_answers
           (user_test_id, question_id, question_answer, correct, answered_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?)
         ON CONFLICT(user_test_id, question_id) DO UPDATE SET
           question_answer = excluded.question_answer,
           correct = excluded.correct,
           answered_at = excluded.answered_at,
           updated_at = excluded.updated_at`,
        [
          a.userTestId,
          a.questionId,
          a.questionAnswer ?? null,
          a.correct ?? 0,
          a.answeredAt ?? null,
          now,
        ]
      );
    }
    for (const t of pull.testsFinished ?? []) {
      await db.run(
        `INSERT INTO sync_pull_tests_finished
           (user_test_id, percentage, passed, time_end, updated_at)
         VALUES (?, ?, ?, ?, ?)
         ON CONFLICT(user_test_id) DO UPDATE SET
           percentage = excluded.percentage,
           passed = excluded.passed,
           time_end = excluded.time_end,
           updated_at = excluded.updated_at`,
        [t.userTestId, t.percentage ?? null, t.passed ?? null, t.timeEnd ?? null, now]
      );
    }
    for (const goal of pull.courseGoals ?? []) {
      await this.upsertLocalCourseGoal({
        courseId: goal.courseId,
        targetDate: goal.targetDate,
        weeklyTrainingDays: goal.weeklyTrainingDays,
        includeWeekends: goal.includeWeekends,
        dailyQuestionTarget: goal.dailyQuestionTarget,
        preferredSessionSize: goal.preferredSessionSize,
        maxDailySuggestedQuestions: goal.maxDailySuggestedQuestions,
        updatedAt: goal.updatedAt ?? now,
      });
    }
    for (const reset of pull.subjectResets ?? []) {
      await this.upsertLocalSubjectReset(reset.subjectId, reset.resetAt, reset.updatedAt ?? reset.resetAt ?? now);
    }
  }

  async upsertLocalCourseGoal(goal: {
    courseId: number;
    targetDate: string;
    weeklyTrainingDays: number;
    includeWeekends: number;
    dailyQuestionTarget: number;
    preferredSessionSize: number;
    maxDailySuggestedQuestions: number;
    updatedAt?: string;
  }): Promise<void> {
    const db = await this.requireDb();
    const updatedAt = goal.updatedAt ?? new Date().toISOString();
    await db.run(
      `INSERT INTO local_course_goal
         (course_id, target_date, weekly_training_days, include_weekends, daily_question_target,
          preferred_session_size, max_daily_suggested_questions, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(course_id) DO UPDATE SET
         target_date = excluded.target_date,
         weekly_training_days = excluded.weekly_training_days,
         include_weekends = excluded.include_weekends,
         daily_question_target = excluded.daily_question_target,
         preferred_session_size = excluded.preferred_session_size,
         max_daily_suggested_questions = excluded.max_daily_suggested_questions,
         updated_at = excluded.updated_at`,
      [
        goal.courseId,
        goal.targetDate,
        goal.weeklyTrainingDays ?? 5,
        goal.includeWeekends ?? 0,
        goal.dailyQuestionTarget ?? 20,
        goal.preferredSessionSize ?? 20,
        goal.maxDailySuggestedQuestions ?? 240,
        updatedAt,
      ]
    );
  }

  async getLocalCourseGoal(courseId: number): Promise<{
    courseId: number;
    targetDate: string;
    weeklyTrainingDays: number;
    includeWeekends: number;
    dailyQuestionTarget: number;
    preferredSessionSize: number;
    maxDailySuggestedQuestions: number;
    updatedAt: string;
  } | null> {
    const db = await this.requireDb();
    const res = await db.query(`SELECT * FROM local_course_goal WHERE course_id = ? LIMIT 1`, [courseId]);
    const row = res.values?.[0];
    if (!row) return null;
    return {
      courseId: Number(row['course_id']),
      targetDate: String(row['target_date']),
      weeklyTrainingDays: Number(row['weekly_training_days'] ?? 5),
      includeWeekends: Number(row['include_weekends'] ?? 0),
      dailyQuestionTarget: Number(row['daily_question_target'] ?? 20),
      preferredSessionSize: Number(row['preferred_session_size'] ?? 20),
      maxDailySuggestedQuestions: Number(row['max_daily_suggested_questions'] ?? 240),
      updatedAt: String(row['updated_at'] ?? ''),
    };
  }

  async upsertLocalSubjectReset(subjectId: number, resetAt: string, updatedAt?: string): Promise<void> {
    const db = await this.requireDb();
    await db.run(
      `INSERT INTO local_subject_reset (subject_id, reset_at, updated_at)
       VALUES (?, ?, ?)
       ON CONFLICT(subject_id) DO UPDATE SET
         reset_at = excluded.reset_at,
         updated_at = excluded.updated_at`,
      [subjectId, resetAt, updatedAt ?? resetAt]
    );
  }

  async upsertRetentionCache(cacheKey: string, payloadJson: string): Promise<void> {
    const db = await this.requireDb();
    await db.run(
      `INSERT INTO local_retention_cache (cache_key, payload_json, updated_at)
       VALUES (?, ?, ?)
       ON CONFLICT(cache_key) DO UPDATE SET
         payload_json = excluded.payload_json,
         updated_at = excluded.updated_at`,
      [cacheKey, payloadJson, new Date().toISOString()]
    );
  }

  async getRetentionCache(cacheKey: string): Promise<string | null> {
    const db = await this.requireDb();
    const res = await db.query(`SELECT payload_json FROM local_retention_cache WHERE cache_key = ? LIMIT 1`, [cacheKey]);
    const row = res.values?.[0];
    return row ? String(row['payload_json']) : null;
  }

  async getOfflineTrainNextStats(courseId: number): Promise<{
    courseId: number;
    totalQuestions: number;
    questionsSeen: number;
    subjectId: number | null;
    subjectName: string | null;
    subjectTotal: number;
    subjectSeen: number;
    subjectWrong: number;
  } | null> {
    const db = await this.requireDb();
    const manifests = await db.query(
      `SELECT subject_id, question_count FROM pack_manifest WHERE course_id = ? ORDER BY subject_id ASC`,
      [courseId]
    );
    const subjects = manifests.values ?? [];
    if (subjects.length === 0) return null;
    let totalQuestions = 0;
    let questionsSeen = 0;
    let pick: { subjectId: number; total: number; seen: number; wrong: number } | null = null;
    for (const row of subjects) {
      const subjectId = Number(row['subject_id']);
      const total = Number(row['question_count'] ?? 0);
      const seen = await this.countSeenForSubject(db, courseId, subjectId);
      const wrong = await this.countWrongForSubject(db, courseId, subjectId);
      totalQuestions += total;
      questionsSeen += seen;
      if (!pick && seen < total) {
        pick = { subjectId, total, seen, wrong };
      }
    }
    if (!pick) {
      const first = subjects[0];
      const subjectId = Number(first['subject_id']);
      pick = {
        subjectId,
        total: Number(first['question_count'] ?? 0),
        seen: await this.countSeenForSubject(db, courseId, subjectId),
        wrong: await this.countWrongForSubject(db, courseId, subjectId),
      };
    }
    return {
      courseId,
      totalQuestions,
      questionsSeen,
      subjectId: pick.subjectId,
      subjectName: `Subject #${pick.subjectId}`,
      subjectTotal: pick.total,
      subjectSeen: pick.seen,
      subjectWrong: pick.wrong,
    };
  }

  async upsertPackManifest(
    packId: string,
    courseId: number,
    subjectId: number,
    contentVersion: string,
    packSchemaVersion: number,
    packSchemaHash: string,
    questionCount: number
  ): Promise<void> {
    const db = await this.requireDb();
    const now = new Date().toISOString();
    await db.run(
      `INSERT INTO pack_manifest
         (pack_id, course_id, subject_id, content_version, pack_schema_version, pack_schema_hash, question_count, installed_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(pack_id) DO UPDATE SET
         content_version = excluded.content_version,
         pack_schema_version = excluded.pack_schema_version,
         pack_schema_hash = excluded.pack_schema_hash,
         question_count = excluded.question_count,
         installed_at = excluded.installed_at`,
      [packId, courseId, subjectId, contentVersion, packSchemaVersion, packSchemaHash, questionCount, now]
    );
  }

  async listPackManifests(courseId?: number): Promise<
    { packId: string; courseId: number; subjectId: number; contentVersion: string; packSchemaVersion: number; packSchemaHash: string | null; questionCount: number }[]
  > {
    const db = await this.requireDb();
    const sql = courseId != null
      ? `SELECT pack_id, course_id, subject_id, content_version, pack_schema_version, pack_schema_hash, question_count FROM pack_manifest WHERE course_id = ?`
      : `SELECT pack_id, course_id, subject_id, content_version, pack_schema_version, pack_schema_hash, question_count FROM pack_manifest`;
    const res = await db.query(sql, courseId != null ? [courseId] : []);
    return (res.values ?? []).map((r) => ({
      packId: String(r['pack_id']),
      courseId: Number(r['course_id']),
      subjectId: Number(r['subject_id']),
      contentVersion: String(r['content_version']),
      packSchemaVersion: Number(r['pack_schema_version'] ?? 1),
      packSchemaHash: r['pack_schema_hash'] != null ? String(r['pack_schema_hash']) : null,
      questionCount: Number(r['question_count'] ?? 0),
    }));
  }

  async listInstalledCourseIds(): Promise<number[]> {
    const db = await this.requireDb();
    const res = await db.query(`SELECT DISTINCT course_id FROM pack_manifest ORDER BY course_id ASC`);
    return (res.values ?? []).map((row) => Number(row['course_id']));
  }

  async getPackManifest(courseId: number, subjectId: number): Promise<
    { packId: string; courseId: number; subjectId: number; contentVersion: string; packSchemaVersion: number; packSchemaHash: string | null; questionCount: number } | null
  > {
    const db = await this.requireDb();
    const res = await db.query(
      `SELECT pack_id, course_id, subject_id, content_version, pack_schema_version, pack_schema_hash, question_count
       FROM pack_manifest
       WHERE course_id = ? AND subject_id = ?
       LIMIT 1`,
      [courseId, subjectId]
    );
    const row = res.values?.[0];
    if (!row) return null;
    return {
      packId: String(row['pack_id']),
      courseId: Number(row['course_id']),
      subjectId: Number(row['subject_id']),
      contentVersion: String(row['content_version']),
      packSchemaVersion: Number(row['pack_schema_version'] ?? 1),
      packSchemaHash: row['pack_schema_hash'] != null ? String(row['pack_schema_hash']) : null,
      questionCount: Number(row['question_count'] ?? 0),
    };
  }

  async hasInstalledPack(courseId: number, subjectId: number): Promise<boolean> {
    const manifest = await this.getPackManifest(courseId, subjectId);
    if (!manifest) return false;
    const ids = await this.listLocalQuestionIds(courseId, [subjectId]);
    return ids.length > 0;
  }

  async upsertPackDownloadState(
    packId: string,
    courseId: number,
    subjectId: number,
    contentVersion: string,
    state: 'pending' | 'downloading' | 'importing' | 'installed' | 'failed',
    opts?: { lastError?: string | null; resetAttempts?: boolean }
  ): Promise<void> {
    const db = await this.requireDb();
    const now = new Date().toISOString();
    const existing = await db.query(
      `SELECT attempt_count FROM pack_download WHERE pack_id = ?`,
      [packId]
    );
    const attemptCount = opts?.resetAttempts
      ? 0
      : Number(existing.values?.[0]?.['attempt_count'] ?? 0);
    await db.run(
      `INSERT INTO pack_download
         (pack_id, course_id, subject_id, content_version, state, attempt_count, last_error, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(pack_id) DO UPDATE SET
         course_id = excluded.course_id,
         subject_id = excluded.subject_id,
         content_version = excluded.content_version,
         state = excluded.state,
         attempt_count = excluded.attempt_count,
         last_error = excluded.last_error,
         updated_at = excluded.updated_at`,
      [packId, courseId, subjectId, contentVersion, state, attemptCount, opts?.lastError ?? null, now]
    );
  }

  async markPackDownloadFailed(
    packId: string,
    courseId: number,
    subjectId: number,
    contentVersion: string,
    error: string
  ): Promise<void> {
    const db = await this.requireDb();
    const now = new Date().toISOString();
    const existing = await db.query(
      `SELECT attempt_count FROM pack_download WHERE pack_id = ?`,
      [packId]
    );
    const attemptCount = Number(existing.values?.[0]?.['attempt_count'] ?? 0) + 1;
    await db.run(
      `INSERT INTO pack_download
         (pack_id, course_id, subject_id, content_version, state, attempt_count, last_error, updated_at)
       VALUES (?, ?, ?, ?, 'failed', ?, ?, ?)
       ON CONFLICT(pack_id) DO UPDATE SET
         state = 'failed',
         attempt_count = excluded.attempt_count,
         last_error = excluded.last_error,
         updated_at = excluded.updated_at`,
      [packId, courseId, subjectId, contentVersion, attemptCount, error, now]
    );
  }

  async getPackDownloadState(courseId: number, subjectId: number): Promise<
    { state: string; attemptCount: number; lastError: string | null; contentVersion: string } | null
  > {
    const db = await this.requireDb();
    const res = await db.query(
      `SELECT state, attempt_count, last_error, content_version
       FROM pack_download
       WHERE course_id = ? AND subject_id = ?
       ORDER BY updated_at DESC
       LIMIT 1`,
      [courseId, subjectId]
    );
    const row = res.values?.[0];
    if (!row) return null;
    return {
      state: String(row['state']),
      attemptCount: Number(row['attempt_count'] ?? 0),
      lastError: row['last_error'] != null ? String(row['last_error']) : null,
      contentVersion: String(row['content_version']),
    };
  }

  async importContentPack(
    manifest: ContentPackManifest,
    questionIds: number[],
    questions: ContentPackQuestion[]
  ): Promise<void> {
    const db = await this.requireDb();
    await this.clearSubjectPack(manifest.courseId, manifest.subjectId);
    await this.upsertPackManifest(
      manifest.packId,
      manifest.courseId,
      manifest.subjectId,
      manifest.contentVersion,
      manifest.packSchemaVersion,
      manifest.packSchemaHash,
      manifest.questionCount
    );

    const order = new Map<number, number>();
    questionIds.forEach((id, index) => order.set(Number(id), index));
    for (const question of questions) {
      await db.run(
        `INSERT OR REPLACE INTO pack_question
           (course_id, subject_id, question_id, position, payload_json)
         VALUES (?, ?, ?, ?, ?)`,
        [
          manifest.courseId,
          manifest.subjectId,
          question.id,
          order.get(Number(question.id)) ?? question.id,
          JSON.stringify(question),
        ]
      );
    }
  }

  async clearDownloadedPacks(): Promise<void> {
    const db = await this.requireDb();
    await db.execute(`
      DELETE FROM pack_question;
      DELETE FROM pack_manifest;
      DELETE FROM pack_download;
    `);
  }

  async listDownloadedPackStats(): Promise<
    { courseId: number; subjectId: number; questionCount: number; payloadBytes: number }[]
  > {
    const db = await this.requireDb();
    const res = await db.query(
      `SELECT m.course_id, m.subject_id, m.question_count,
              COALESCE(SUM(LENGTH(CAST(q.payload_json AS BLOB))), 0) AS payload_bytes
       FROM pack_manifest m
       LEFT JOIN pack_question q
         ON q.course_id = m.course_id AND q.subject_id = m.subject_id
       GROUP BY m.course_id, m.subject_id
       ORDER BY m.course_id ASC, m.subject_id ASC`
    );
    return (res.values ?? []).map((row) => ({
      courseId: Number(row['course_id']),
      subjectId: Number(row['subject_id']),
      questionCount: Number(row['question_count'] ?? 0),
      payloadBytes: Number(row['payload_bytes'] ?? 0),
    }));
  }

  async listPackStatuses(): Promise<
    {
      courseId: number;
      subjectId: number;
      contentVersion: string | null;
      state: 'pending' | 'downloading' | 'importing' | 'installed' | 'failed' | 'unknown';
      attemptCount: number;
      lastError: string | null;
      questionCount: number;
      payloadBytes: number;
      hasManifest: boolean;
      updatedAt: string | null;
    }[]
  > {
    const db = await this.requireDb();
    const manifests = await db.query(
      `SELECT m.course_id, m.subject_id, m.content_version, m.question_count,
              COALESCE(SUM(LENGTH(CAST(q.payload_json AS BLOB))), 0) AS payload_bytes
       FROM pack_manifest m
       LEFT JOIN pack_question q
         ON q.course_id = m.course_id AND q.subject_id = m.subject_id
       GROUP BY m.course_id, m.subject_id`
    );
    const downloads = await db.query(
      `SELECT course_id, subject_id, content_version, state, attempt_count, last_error, updated_at
       FROM pack_download`
    );

    type Entry = {
      courseId: number;
      subjectId: number;
      contentVersion: string | null;
      state: 'pending' | 'downloading' | 'importing' | 'installed' | 'failed' | 'unknown';
      attemptCount: number;
      lastError: string | null;
      questionCount: number;
      payloadBytes: number;
      hasManifest: boolean;
      updatedAt: string | null;
    };

    const map = new Map<string, Entry>();
    const key = (courseId: number, subjectId: number) => `${courseId}:${subjectId}`;

    for (const row of manifests.values ?? []) {
      const courseId = Number(row['course_id']);
      const subjectId = Number(row['subject_id']);
      map.set(key(courseId, subjectId), {
        courseId,
        subjectId,
        contentVersion: row['content_version'] != null ? String(row['content_version']) : null,
        state: 'installed',
        attemptCount: 0,
        lastError: null,
        questionCount: Number(row['question_count'] ?? 0),
        payloadBytes: Number(row['payload_bytes'] ?? 0),
        hasManifest: true,
        updatedAt: null,
      });
    }

    for (const row of downloads.values ?? []) {
      const courseId = Number(row['course_id']);
      const subjectId = Number(row['subject_id']);
      const k = key(courseId, subjectId);
      const state = String(row['state']) as Entry['state'];
      const existing = map.get(k);
      if (existing) {
        if (state !== 'installed') {
          existing.state = state;
        }
        existing.attemptCount = Number(row['attempt_count'] ?? 0);
        existing.lastError = row['last_error'] != null ? String(row['last_error']) : null;
        existing.updatedAt = row['updated_at'] != null ? String(row['updated_at']) : null;
        if (!existing.contentVersion && row['content_version'] != null) {
          existing.contentVersion = String(row['content_version']);
        }
      } else {
        map.set(k, {
          courseId,
          subjectId,
          contentVersion: row['content_version'] != null ? String(row['content_version']) : null,
          state,
          attemptCount: Number(row['attempt_count'] ?? 0),
          lastError: row['last_error'] != null ? String(row['last_error']) : null,
          questionCount: 0,
          payloadBytes: 0,
          hasManifest: false,
          updatedAt: row['updated_at'] != null ? String(row['updated_at']) : null,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) =>
      a.courseId === b.courseId ? a.subjectId - b.subjectId : a.courseId - b.courseId
    );
  }

  async clearSubjectPack(courseId: number, subjectId: number): Promise<void> {
    const db = await this.requireDb();
    await db.run(`DELETE FROM pack_question WHERE course_id = ? AND subject_id = ?`, [courseId, subjectId]);
    await db.run(`DELETE FROM pack_manifest WHERE course_id = ? AND subject_id = ?`, [courseId, subjectId]);
  }

  async clearSubjectPackDownloadState(courseId: number, subjectId: number): Promise<void> {
    const db = await this.requireDb();
    await db.run(`DELETE FROM pack_download WHERE course_id = ? AND subject_id = ?`, [courseId, subjectId]);
  }

  async listLocalQuestionIds(courseId: number, subjectIds: number[]): Promise<number[]> {
    const db = await this.requireDb();
    if (subjectIds.length === 0) return [];
    const placeholders = subjectIds.map(() => '?').join(',');
    const res = await db.query(
      `SELECT question_id
       FROM pack_question
       WHERE course_id = ? AND subject_id IN (${placeholders})
       ORDER BY subject_id ASC, position ASC`,
      [courseId, ...subjectIds]
    );
    return (res.values ?? []).map((row) => Number(row['question_id']));
  }

  async getLocalQuestions(courseId: number, subjectIds: number[], questionIds: number[]): Promise<ContentPackQuestion[]> {
    const db = await this.requireDb();
    if (subjectIds.length === 0 || questionIds.length === 0) return [];
    const subjectPlaceholders = subjectIds.map(() => '?').join(',');
    const questionPlaceholders = questionIds.map(() => '?').join(',');
    const res = await db.query(
      `SELECT payload_json
       FROM pack_question
       WHERE course_id = ?
         AND subject_id IN (${subjectPlaceholders})
         AND question_id IN (${questionPlaceholders})`,
      [courseId, ...subjectIds, ...questionIds]
    );
    return (res.values ?? []).map((row) => JSON.parse(String(row['payload_json'])) as ContentPackQuestion);
  }

  async getPullNote(questionId: number): Promise<string | null> {
    const db = await this.requireDb();
    const res = await db.query(
      `SELECT notes FROM sync_pull_notes WHERE question_id = ?`,
      [questionId]
    );
    const v = res.values?.[0]?.['notes'];
    return v != null ? String(v) : null;
  }

  private async countSeenForSubject(db: SQLiteDBConnection, courseId: number, subjectId: number): Promise<number> {
    const res = await db.query(
      `SELECT COUNT(DISTINCT a.question_id) AS c
       FROM sync_pull_test_answers a
       JOIN pack_question pq ON pq.question_id = a.question_id
       LEFT JOIN local_subject_reset sr ON sr.subject_id = pq.subject_id
       WHERE pq.course_id = ?
         AND pq.subject_id = ?
         AND a.answered_at IS NOT NULL
         AND a.answered_at > COALESCE(sr.reset_at, '')`,
      [courseId, subjectId]
    );
    return Number(res.values?.[0]?.['c'] ?? 0);
  }

  private async countWrongForSubject(db: SQLiteDBConnection, courseId: number, subjectId: number): Promise<number> {
    const res = await db.query(
      `SELECT COUNT(*) AS c FROM (
         SELECT a.question_id, a.correct,
                ROW_NUMBER() OVER (PARTITION BY a.question_id ORDER BY a.answered_at DESC) AS rn
         FROM sync_pull_test_answers a
         JOIN pack_question pq ON pq.question_id = a.question_id
         LEFT JOIN local_subject_reset sr ON sr.subject_id = pq.subject_id
         WHERE pq.course_id = ?
           AND pq.subject_id = ?
           AND a.answered_at IS NOT NULL
           AND a.answered_at > COALESCE(sr.reset_at, '')
       ) latest
       WHERE latest.rn = 1 AND latest.correct = 0`,
      [courseId, subjectId]
    );
    return Number(res.values?.[0]?.['c'] ?? 0);
  }

  private async requireDb(): Promise<SQLiteDBConnection> {
    await this.init();
    if (!this.db) {
      throw new Error('SQLite not available');
    }
    return this.db;
  }

  private async openNative(): Promise<void> {
    try {
      const { CapacitorSQLite, SQLiteConnection } = await import('@capacitor-community/sqlite');
      this.connection = new SQLiteConnection(CapacitorSQLite);
      await this.ensureEncryptionSecret(CapacitorSQLite);
      await this.connection.checkConnectionsConsistency();
      const existing = await this.connection.isConnection(DB_NAME, false);
      this.db = existing.result
        ? await this.connection.retrieveConnection(DB_NAME, false)
        : await this.connection.createConnection(DB_NAME, true, 'secret', 1, false);
      await this.db.open();
      await this.db.execute(`
        CREATE TABLE IF NOT EXISTS sync_meta (
          key TEXT PRIMARY KEY NOT NULL,
          value TEXT
        );
        CREATE TABLE IF NOT EXISTS pending_events (
          event_id TEXT PRIMARY KEY NOT NULL,
          event_type TEXT NOT NULL,
          payload TEXT NOT NULL,
          created_at TEXT NOT NULL,
          attempt_count INTEGER NOT NULL DEFAULT 0,
          last_error TEXT,
          status TEXT NOT NULL DEFAULT 'pending'
        );
        CREATE TABLE IF NOT EXISTS sync_state (
          id INTEGER PRIMARY KEY CHECK (id = 1),
          state TEXT NOT NULL DEFAULT 'idle',
          last_success_at TEXT,
          last_error TEXT
        );
        INSERT OR IGNORE INTO sync_state (id, state) VALUES (1, 'idle');
        CREATE TABLE IF NOT EXISTS sync_pull_notes (
          question_id INTEGER PRIMARY KEY NOT NULL,
          notes TEXT NOT NULL,
          updated_at TEXT NOT NULL
        );
        CREATE TABLE IF NOT EXISTS sync_pull_flags (
          question_id INTEGER PRIMARY KEY NOT NULL,
          flag_id INTEGER,
          hide INTEGER NOT NULL DEFAULT 0,
          updated_at TEXT NOT NULL
        );
        CREATE TABLE IF NOT EXISTS sync_pull_test_answers (
          user_test_id INTEGER NOT NULL,
          question_id INTEGER NOT NULL,
          question_answer TEXT,
          correct INTEGER,
          answered_at TEXT,
          updated_at TEXT NOT NULL,
          PRIMARY KEY (user_test_id, question_id)
        );
        CREATE TABLE IF NOT EXISTS sync_pull_tests_finished (
          user_test_id INTEGER PRIMARY KEY NOT NULL,
          percentage REAL,
          passed INTEGER,
          time_end TEXT,
          updated_at TEXT NOT NULL
        );
        CREATE TABLE IF NOT EXISTS local_course_goal (
          course_id INTEGER PRIMARY KEY NOT NULL,
          target_date TEXT NOT NULL,
          weekly_training_days INTEGER NOT NULL DEFAULT 5,
          include_weekends INTEGER NOT NULL DEFAULT 0,
          daily_question_target INTEGER NOT NULL DEFAULT 20,
          preferred_session_size INTEGER NOT NULL DEFAULT 20,
          max_daily_suggested_questions INTEGER NOT NULL DEFAULT 240,
          updated_at TEXT NOT NULL
        );
        CREATE TABLE IF NOT EXISTS local_subject_reset (
          subject_id INTEGER PRIMARY KEY NOT NULL,
          reset_at TEXT NOT NULL,
          updated_at TEXT NOT NULL
        );
        CREATE TABLE IF NOT EXISTS local_retention_cache (
          cache_key TEXT PRIMARY KEY NOT NULL,
          payload_json TEXT NOT NULL,
          updated_at TEXT NOT NULL
        );
        CREATE TABLE IF NOT EXISTS local_offline_entitlement (
          user_id INTEGER PRIMARY KEY NOT NULL,
          token TEXT NOT NULL,
          expires_at TEXT NOT NULL,
          updated_at TEXT NOT NULL
        );
        CREATE TABLE IF NOT EXISTS pack_manifest (
          pack_id TEXT PRIMARY KEY NOT NULL,
          course_id INTEGER NOT NULL,
          subject_id INTEGER NOT NULL,
          content_version TEXT NOT NULL,
          pack_schema_version INTEGER NOT NULL DEFAULT 1,
          pack_schema_hash TEXT,
          question_count INTEGER NOT NULL DEFAULT 0,
          installed_at TEXT
        );
        CREATE TABLE IF NOT EXISTS pack_question (
          course_id INTEGER NOT NULL,
          subject_id INTEGER NOT NULL,
          question_id INTEGER NOT NULL,
          position INTEGER NOT NULL,
          payload_json TEXT NOT NULL,
          PRIMARY KEY (course_id, subject_id, question_id)
        );
        CREATE TABLE IF NOT EXISTS pack_download (
          pack_id TEXT PRIMARY KEY NOT NULL,
          course_id INTEGER NOT NULL,
          subject_id INTEGER NOT NULL,
          content_version TEXT NOT NULL,
          state TEXT NOT NULL,
          attempt_count INTEGER NOT NULL DEFAULT 0,
          last_error TEXT,
          updated_at TEXT NOT NULL
        );
      `);
      await this.ensureColumn('pack_manifest', 'pack_schema_version', 'INTEGER NOT NULL DEFAULT 1');
      await this.ensureColumn('pack_manifest', 'pack_schema_hash', 'TEXT');
      LoggerService.log('[LocalDatabaseService] SQLite ready', { db: DB_NAME });
    } catch (e) {
      LoggerService.error('[LocalDatabaseService] Failed to open SQLite', e);
      this.db = null;
      this.connection = null;
    }
  }

  private async ensureColumn(table: string, column: string, definition: string): Promise<void> {
    if (!this.db) return;
    const res = await this.db.query(`PRAGMA table_info(${table})`);
    const exists = (res.values ?? []).some((row) => String(row['name']) === column);
    if (!exists) {
      await this.db.execute(`ALTER TABLE ${table} ADD COLUMN ${column} ${definition}`);
    }
  }

  private async ensureEncryptionSecret(capacitorSQLite: {
    isSecretStored?: () => Promise<{ result?: boolean }>;
    setEncryptionSecret?: (options: { passphrase: string }) => Promise<void>;
  }): Promise<void> {
    if (!capacitorSQLite.isSecretStored || !capacitorSQLite.setEncryptionSecret) {
      return;
    }
    const stored = await capacitorSQLite.isSecretStored();
    if (stored.result) {
      return;
    }
    const passphrase = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    await capacitorSQLite.setEncryptionSecret({ passphrase });
  }
}
